import { useEffect, useMemo, useState, type MouseEvent as ReactMouseEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { api, type PlatformServiceKey, type TenantBillingStatus, type TenantFaciliooDebugState, type TenantRow } from "../app/api";
import { ServerDataTable } from "../components/tables/ServerDataTable";
import { isActionMenuEventTarget, useActionMenuAutoClose } from "../hooks/useActionMenuAutoClose";

type TenantEditForm = {
  name: string;
  status: "aktiv" | "inaktiv";
  legalForm: string;
  website: string;
  contactEmail: string;
  contactPhone: string;
  addressStreet: string;
  addressPostalCode: string;
  addressCity: string;
  addressCountry: string;
  contactSalutation: string;
  contactTitle: string;
  contactFirstName: string;
  contactLastName: string;
  contactPersonEmail: string;
  contactPersonPhone: string;
  technicalTimezone: string;
  technicalLocale: string;
  technicalCurrency: string;
  technicalUiFramework: "classic" | "mockup_v2";
  defaultStimmrechtsart: "kopf" | "stimmrecht" | "mea";
  defaultQuorum: "allstimmig" | "einfache_mehrheit" | "qualifizierte_mehrheit";
  defaultFaciliooEnabled: boolean;
  trialEnabled: boolean;
  trialDays: number;
  adminUsername: string;
  adminDisplayName: string;
  adminPassword: string;
};

const mapTenantToEditForm = (row: TenantRow): TenantEditForm => {
  const profile = row.profile;
  const startsAt = profile?.trial?.startsAt ? Date.parse(profile.trial.startsAt) : NaN;
  const endsAt = profile?.trial?.endsAt ? Date.parse(profile.trial.endsAt) : NaN;
  const computedTrialDays =
    Number.isFinite(startsAt) && Number.isFinite(endsAt) && endsAt > startsAt
      ? Math.max(1, Math.ceil((endsAt - startsAt) / (24 * 60 * 60 * 1000)))
      : 14;

  return {
    name: row.name,
    status: row.status,
    legalForm: profile?.legalForm ?? "",
    website: profile?.website ?? "",
    contactEmail: profile?.contactEmail ?? "",
    contactPhone: profile?.contactPhone ?? "",
    addressStreet: profile?.address?.street ?? "",
    addressPostalCode: profile?.address?.postalCode ?? "",
    addressCity: profile?.address?.city ?? "",
    addressCountry: profile?.address?.country ?? "DE",
    contactSalutation: profile?.primaryContact?.salutation ?? "",
    contactTitle: profile?.primaryContact?.title ?? "",
    contactFirstName: profile?.primaryContact?.firstName ?? "",
    contactLastName: profile?.primaryContact?.lastName ?? "",
    contactPersonEmail: profile?.primaryContact?.email ?? "",
    contactPersonPhone: profile?.primaryContact?.phone ?? "",
    technicalTimezone: profile?.technical?.timezone ?? "Europe/Berlin",
    technicalLocale: profile?.technical?.locale ?? "de-DE",
    technicalCurrency: profile?.technical?.currency ?? "EUR",
    technicalUiFramework: profile?.technical?.uiFramework === "mockup_v2" ? "mockup_v2" : "classic",
    defaultStimmrechtsart: profile?.defaults?.stimmrechtsart ?? "kopf",
    defaultQuorum: profile?.defaults?.quorum ?? "allstimmig",
    defaultFaciliooEnabled: profile?.defaults?.faciliooEnabled ?? false,
    trialEnabled: Boolean(profile?.trial?.startsAt && profile?.trial?.endsAt),
    trialDays: computedTrialDays,
    adminUsername: row.primaryAdmin?.username ?? "",
    adminDisplayName: row.primaryAdmin?.displayName ?? "",
    adminPassword: ""
  };
};

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return "-";
  return new Intl.DateTimeFormat("de-DE", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  }).format(new Date(parsed));
};

const renderTenantServiceStateBadge = (statusText: "ok" | "nicht_ok" | "inaktiv" | "nicht_konfiguriert") => {
  if (statusText === "ok") return <span className="badge aktiv">aktiv</span>;
  if (statusText === "nicht_ok") {
    return (
      <span className="badge inaktiv" style={{ borderColor: "#dc2626", color: "#dc2626" }}>
        störung
      </span>
    );
  }
  if (statusText === "nicht_konfiguriert") {
    return (
      <span className="badge inaktiv" style={{ borderColor: "#f59e0b", color: "#b45309" }}>
        nicht konfiguriert
      </span>
    );
  }
  return <span className="badge inaktiv">inaktiv</span>;
};

const billingTierLabel = (tier?: TenantBillingStatus["currentTier"] | NonNullable<TenantBillingStatus["resolvedTier"]>["tier"] | null) => {
  if (tier === "tier_a") return "1-500 Einheiten";
  if (tier === "tier_b") return "501-1000 Einheiten";
  if (tier === "tier_c") return "1001-2500 Einheiten";
  if (tier === "variable") return "ab 2501 variabel";
  return "-";
};

const billingStatusBadge = (status?: string) => {
  const normalized = String(status ?? "not_started").trim().toLowerCase();
  if (normalized === "active" || normalized === "trialing") return <span className="badge aktiv">{normalized}</span>;
  if (normalized === "not_started" || normalized === "customer_created" || normalized === "checkout_created" || normalized === "snapshot_created") {
    return (
      <span className="badge inaktiv" style={{ borderColor: "#f59e0b", color: "#b45309" }}>
        {normalized}
      </span>
    );
  }
  return <span className="badge inaktiv">{normalized}</span>;
};

const isSystemTenantSlug = (slug: string) => {
  const value = String(slug || "").trim().toLowerCase();
  return value === "default" || value === "plattform" || value === "platform";
};

const PasswordVisibilityIcon = ({ hidden }: { hidden: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M2 12C3.8 7.8 7.5 5 12 5c4.5 0 8.2 2.8 10 7-1.8 4.2-5.5 7-10 7-4.5 0-8.2-2.8-10-7Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
    {hidden ? <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /> : null}
  </svg>
);

type MemoryRangeOption = {
  key: "1m" | "2m" | "5m" | "10m" | "30m" | "60m" | "1d";
  label: string;
  minutes: number;
};

const MEMORY_RANGE_OPTIONS: MemoryRangeOption[] = [
  { key: "1m", label: "1 Min", minutes: 1 },
  { key: "2m", label: "2 Min", minutes: 2 },
  { key: "5m", label: "5 Min", minutes: 5 },
  { key: "10m", label: "10 Min", minutes: 10 },
  { key: "30m", label: "30 Min", minutes: 30 },
  { key: "60m", label: "60 Min", minutes: 60 },
  { key: "1d", label: "1 Tag", minutes: 24 * 60 }
];
const MEMORY_RANGE_STORAGE_KEY = "platform_ram_debug_range";
const MEMORY_DEBUG_ENABLED_STORAGE_KEY = "platform_ram_debug_enabled";
const resolveMemoryRefetchIntervalMs = (rangeKey: MemoryRangeOption["key"]) => {
  if (rangeKey === "1m") return 5_000;
  if (rangeKey === "2m") return 10_000;
  if (rangeKey === "5m") return 15_000;
  if (rangeKey === "10m") return 20_000;
  if (rangeKey === "30m") return 30_000;
  if (rangeKey === "60m") return 60_000;
  return 5 * 60_000;
};

const emptyTenantFaciliooDebugState: TenantFaciliooDebugState = {
  tenantSlug: "",
  debugModeEnabled: false,
  debugModeExpiresAt: "",
  deepProbeEnabled: false,
  deepProbeExpiresAt: ""
};

export const PlatformTenantsPage = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createInfo, setCreateInfo] = useState("");
  const [editError, setEditError] = useState("");
  const [editInfo, setEditInfo] = useState("");
  const [editFaciliooDebugError, setEditFaciliooDebugError] = useState("");
  const [editFaciliooDebugInfo, setEditFaciliooDebugInfo] = useState("");
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [editFaciliooDebug, setEditFaciliooDebug] = useState<TenantFaciliooDebugState>(emptyTenantFaciliooDebugState);
  const [editFaciliooDebugLoading, setEditFaciliooDebugLoading] = useState(false);
  const [editForm, setEditForm] = useState<TenantEditForm>({
    name: "",
    status: "aktiv",
    legalForm: "",
    website: "",
    contactEmail: "",
    contactPhone: "",
    addressStreet: "",
    addressPostalCode: "",
    addressCity: "",
    addressCountry: "DE",
    contactSalutation: "",
    contactTitle: "",
    contactFirstName: "",
    contactLastName: "",
    contactPersonEmail: "",
    contactPersonPhone: "",
    technicalTimezone: "Europe/Berlin",
    technicalLocale: "de-DE",
    technicalCurrency: "EUR",
    technicalUiFramework: "classic",
    defaultStimmrechtsart: "kopf",
    defaultQuorum: "allstimmig",
    defaultFaciliooEnabled: false,
    trialEnabled: false,
    trialDays: 14,
    adminUsername: "",
    adminDisplayName: "",
    adminPassword: ""
  });
  const [lastAccessUrl, setLastAccessUrl] = useState("");
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [showEditAdminPassword, setShowEditAdminPassword] = useState(false);
  const [serviceActionInfo, setServiceActionInfo] = useState("");
  const [serviceActionError, setServiceActionError] = useState("");
  const [tenantProvisioningInfo, setTenantProvisioningInfo] = useState("");
  const [tenantProvisioningError, setTenantProvisioningError] = useState("");
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [actionMenuPosition, setActionMenuPosition] = useState<{ left: number; top: number } | null>(null);
  const [isActionMenuHovered, setIsActionMenuHovered] = useState(false);
  const [memoryRange, setMemoryRange] = useState<MemoryRangeOption["key"]>(() => {
    if (typeof window === "undefined") return "10m";
    const raw = window.localStorage.getItem(MEMORY_RANGE_STORAGE_KEY);
    const allowed = new Set(MEMORY_RANGE_OPTIONS.map((entry) => entry.key));
    return raw && allowed.has(raw as MemoryRangeOption["key"]) ? (raw as MemoryRangeOption["key"]) : "10m";
  });
  const [memoryDebugEnabled, setMemoryDebugEnabled] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const raw = String(window.localStorage.getItem(MEMORY_DEBUG_ENABLED_STORAGE_KEY) ?? "")
      .trim()
      .toLowerCase();
    return !["0", "false", "no", "off"].includes(raw);
  });
  const [memoryHoverIndex, setMemoryHoverIndex] = useState<number | null>(null);
  const [form, setForm] = useState({
    slug: "",
    name: "",
    legalForm: "",
    website: "",
    contactEmail: "",
    contactPhone: "",
    addressStreet: "",
    addressPostalCode: "",
    addressCity: "",
    addressCountry: "DE",
    contactSalutation: "",
    contactTitle: "",
    contactFirstName: "",
    contactLastName: "",
    contactPersonEmail: "",
    contactPersonPhone: "",
    technicalTimezone: "Europe/Berlin",
    technicalLocale: "de-DE",
    technicalCurrency: "EUR",
    technicalUiFramework: "classic" as "classic" | "mockup_v2",
    defaultStimmrechtsart: "kopf" as "kopf" | "stimmrecht" | "mea",
    defaultQuorum: "allstimmig" as "allstimmig" | "einfache_mehrheit" | "qualifizierte_mehrheit",
    defaultFaciliooEnabled: false,
    trialEnabled: false,
    trialDays: 14,
    adminUsername: "",
    adminDisplayName: "",
    adminPassword: ""
  });
  const memoryHistoryMinutesForFetch = useMemo(
    () => MEMORY_RANGE_OPTIONS.find((option) => option.key === memoryRange)?.minutes ?? 10,
    [memoryRange]
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["tenants"],
    queryFn: () => api.tenants()
  });
  const {
    data: servicesStatus,
    isLoading: servicesLoading,
    isError: servicesIsError,
    error: servicesError
  } = useQuery({
    queryKey: ["platform-services-status", memoryDebugEnabled ? "memory-on" : "memory-off", memoryRange, memoryHistoryMinutesForFetch],
    queryFn: () => api.getPlatformServicesStatus({ includeMemoryDebug: memoryDebugEnabled, historyMinutes: memoryHistoryMinutesForFetch }),
    refetchInterval: memoryDebugEnabled ? resolveMemoryRefetchIntervalMs(memoryRange) : false,
    refetchIntervalInBackground: false
  });
  const {
    data: billingStatus,
    isLoading: billingLoading,
    isError: billingIsError,
    error: billingError
  } = useQuery({
    queryKey: ["tenant-billing", editingSlug],
    queryFn: () => api.getTenantBilling(editingSlug || ""),
    enabled: Boolean(editingSlug)
  });
  const checkServiceMutation = useMutation({
    mutationFn: (service: PlatformServiceKey) => api.checkPlatformServiceStatus(service),
    onSuccess: async (result) => {
      setServiceActionError("");
      setServiceActionInfo(`Dienst geprüft: ${result.service.service} (${result.service.statusText})`);
      await queryClient.invalidateQueries({ queryKey: ["platform-services-status"] });
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : "Dienstprüfung fehlgeschlagen.";
      setServiceActionInfo("");
      setServiceActionError(message);
    }
  });
  const notifyServiceMutation = useMutation({
    mutationFn: ({ service }: { service: PlatformServiceKey }) => api.notifyPlatformServiceStatus(service),
    onSuccess: (result) => {
      setServiceActionError("");
      const skippedMissingEmail = result.skipped.filter((row) => row.reason === "missing_or_invalid_email").length;
      setServiceActionInfo(
        `Störungsmeldung gesendet (${result.sentCount} Empfänger, Dienst: ${result.service.service})${
          skippedMissingEmail > 0 ? ` | ohne gültige Benutzer-E-Mail: ${skippedMissingEmail}` : ""
        }.`
      );
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : "Benachrichtigung fehlgeschlagen.";
      setServiceActionInfo("");
      setServiceActionError(message);
    }
  });

  const createMutation = useMutation({
    mutationFn: () =>
      api.createTenant({
        slug: form.slug.trim(),
        name: form.name.trim(),
        onboarding: {
          legalForm: form.legalForm.trim() || undefined,
          website: form.website.trim() || undefined,
          contactEmail: form.contactEmail.trim(),
          contactPhone: form.contactPhone.trim() || undefined,
          address:
            form.addressStreet.trim() || form.addressPostalCode.trim() || form.addressCity.trim() || form.addressCountry.trim()
              ? {
                  street: form.addressStreet.trim() || undefined,
                  postalCode: form.addressPostalCode.trim() || undefined,
                  city: form.addressCity.trim() || undefined,
                  country: form.addressCountry.trim() || undefined
                }
              : undefined,
          primaryContact:
            form.contactSalutation.trim() ||
            form.contactTitle.trim() ||
            form.contactFirstName.trim() ||
            form.contactLastName.trim() ||
            form.contactPersonEmail.trim() ||
            form.contactPersonPhone.trim()
              ? {
                  salutation: form.contactSalutation.trim() || undefined,
                  title: form.contactTitle.trim() || undefined,
                  firstName: form.contactFirstName.trim() || undefined,
                  lastName: form.contactLastName.trim() || undefined,
                  email: form.contactPersonEmail.trim() || undefined,
                  phone: form.contactPersonPhone.trim() || undefined
                }
              : undefined,
          technical: {
            timezone: form.technicalTimezone.trim() || "Europe/Berlin",
            locale: form.technicalLocale.trim() || "de-DE",
            currency: form.technicalCurrency.trim() || "EUR",
            uiFramework: form.technicalUiFramework
          },
          defaults: {
            faciliooEnabled: form.defaultFaciliooEnabled,
            stimmrechtsart: form.defaultStimmrechtsart,
            quorum: form.defaultQuorum
          },
          trialDays: form.trialEnabled ? form.trialDays : undefined
        },
        admin: {
          username: form.adminUsername.trim(),
          displayName: form.adminDisplayName.trim(),
          password: form.adminPassword
        }
      }),
    onSuccess: async (result) => {
      setCreateError("");
      setCreateInfo(result.provisioning?.message ?? "");
      setLastAccessUrl(result.accessUrl ?? "");
      setForm({
        slug: "",
        name: "",
        legalForm: "",
        website: "",
        contactEmail: "",
        contactPhone: "",
        addressStreet: "",
        addressPostalCode: "",
        addressCity: "",
        addressCountry: "DE",
        contactSalutation: "",
        contactTitle: "",
        contactFirstName: "",
        contactLastName: "",
        contactPersonEmail: "",
        contactPersonPhone: "",
        technicalTimezone: "Europe/Berlin",
        technicalLocale: "de-DE",
        technicalCurrency: "EUR",
        technicalUiFramework: "classic",
        defaultStimmrechtsart: "kopf",
        defaultQuorum: "allstimmig",
        defaultFaciliooEnabled: false,
        trialEnabled: false,
        trialDays: 14,
        adminUsername: "",
        adminDisplayName: "",
        adminPassword: ""
      });
      setCreateOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
    onError: (err) => setCreateError(err instanceof Error ? err.message : "Mandant konnte nicht angelegt werden")
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { slug: string; form: TenantEditForm }) =>
      api.updateTenant(payload.slug, {
        name: payload.form.name.trim(),
        status: payload.form.status,
        admin: {
          username: payload.form.adminUsername.trim(),
          displayName: payload.form.adminDisplayName.trim(),
          ...(payload.form.adminPassword.trim() ? { password: payload.form.adminPassword.trim() } : {})
        },
        onboarding: {
          legalForm: payload.form.legalForm.trim() || undefined,
          website: payload.form.website.trim() || undefined,
          contactEmail: payload.form.contactEmail.trim() || undefined,
          contactPhone: payload.form.contactPhone.trim() || undefined,
          address:
            payload.form.addressStreet.trim() ||
            payload.form.addressPostalCode.trim() ||
            payload.form.addressCity.trim() ||
            payload.form.addressCountry.trim()
              ? {
                  street: payload.form.addressStreet.trim() || undefined,
                  postalCode: payload.form.addressPostalCode.trim() || undefined,
                  city: payload.form.addressCity.trim() || undefined,
                  country: payload.form.addressCountry.trim() || undefined
                }
              : undefined,
          primaryContact:
            payload.form.contactSalutation.trim() ||
            payload.form.contactTitle.trim() ||
            payload.form.contactFirstName.trim() ||
            payload.form.contactLastName.trim() ||
            payload.form.contactPersonEmail.trim() ||
            payload.form.contactPersonPhone.trim()
              ? {
                  salutation: payload.form.contactSalutation.trim() || undefined,
                  title: payload.form.contactTitle.trim() || undefined,
                  firstName: payload.form.contactFirstName.trim() || undefined,
                  lastName: payload.form.contactLastName.trim() || undefined,
                  email: payload.form.contactPersonEmail.trim() || undefined,
                  phone: payload.form.contactPersonPhone.trim() || undefined
                }
              : undefined,
          technical: {
            timezone: payload.form.technicalTimezone.trim() || "Europe/Berlin",
            locale: payload.form.technicalLocale.trim() || "de-DE",
            currency: payload.form.technicalCurrency.trim() || "EUR",
            uiFramework: payload.form.technicalUiFramework
          },
          defaults: {
            faciliooEnabled: payload.form.defaultFaciliooEnabled,
            stimmrechtsart: payload.form.defaultStimmrechtsart,
            quorum: payload.form.defaultQuorum
          },
          trialDays: payload.form.trialEnabled ? payload.form.trialDays : null
        }
      }),
    onSuccess: async () => {
      setEditError("");
      setEditInfo("Mandant wurde aktualisiert.");
      setEditingSlug(null);
      setShowEditAdminPassword(false);
      await queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
    onError: (err) => setEditError(err instanceof Error ? err.message : "Mandant konnte nicht aktualisiert werden")
  });

  useEffect(() => {
    if (!editingSlug) {
      setEditFaciliooDebug(emptyTenantFaciliooDebugState);
      setEditFaciliooDebugLoading(false);
      setEditFaciliooDebugError("");
      setEditFaciliooDebugInfo("");
      return;
    }
    let cancelled = false;
    const loadFaciliooDebugState = async () => {
      setEditFaciliooDebugLoading(true);
      setEditFaciliooDebugError("");
      try {
        const result = await api.getTenantFaciliooDebug(editingSlug);
        if (cancelled) return;
        setEditFaciliooDebug(result);
      } catch (error) {
        if (cancelled) return;
        setEditFaciliooDebug(emptyTenantFaciliooDebugState);
        setEditFaciliooDebugError(error instanceof Error ? error.message : "Facilioo-Debugstatus konnte nicht geladen werden.");
      } finally {
        if (!cancelled) {
          setEditFaciliooDebugLoading(false);
        }
      }
    };
    void loadFaciliooDebugState();
    return () => {
      cancelled = true;
    };
  }, [editingSlug]);

  const updateFaciliooDebugMutation = useMutation({
    mutationFn: (payload: { slug: string; debugModeEnabled: boolean; deepProbeEnabled: boolean }) =>
      api.updateTenantFaciliooDebug(payload.slug, {
        debugModeEnabled: payload.debugModeEnabled,
        deepProbeEnabled: payload.deepProbeEnabled
      }),
    onSuccess: (result) => {
      setEditFaciliooDebug(result);
      setEditFaciliooDebugError("");
      setEditFaciliooDebugInfo("Facilioo-Debugsteuerung wurde aktualisiert (24h).");
    },
    onError: (error) => {
      setEditFaciliooDebugInfo("");
      setEditFaciliooDebugError(error instanceof Error ? error.message : "Facilioo-Debugsteuerung konnte nicht gespeichert werden.");
    }
  });

  const checkoutBillingMutation = useMutation({
    mutationFn: (slug: string) => api.createTenantBillingCheckout(slug),
    onSuccess: async (result) => {
      setEditError("");
      setEditInfo("Stripe Checkout wurde erstellt.");
      await queryClient.invalidateQueries({ queryKey: ["tenant-billing", editingSlug] });
      if (result.url) window.location.assign(result.url);
    },
    onError: (error) => {
      setEditInfo("");
      setEditError(error instanceof Error ? error.message : "Stripe Checkout konnte nicht erstellt werden.");
    }
  });

  const portalBillingMutation = useMutation({
    mutationFn: (slug: string) => api.createTenantBillingPortal(slug),
    onSuccess: (result) => {
      setEditError("");
      setEditInfo("Stripe Portal wurde geöffnet.");
      window.open(result.url, "_blank", "noopener,noreferrer");
    },
    onError: (error) => {
      setEditInfo("");
      setEditError(error instanceof Error ? error.message : "Stripe Portal konnte nicht geöffnet werden.");
    }
  });

  const syncBillingMutation = useMutation({
    mutationFn: (slug: string) => api.syncTenantBilling(slug),
    onSuccess: async () => {
      setEditError("");
      setEditInfo("Billing-Snapshot wurde synchronisiert.");
      await queryClient.invalidateQueries({ queryKey: ["tenant-billing", editingSlug] });
    },
    onError: (error) => {
      setEditInfo("");
      setEditError(error instanceof Error ? error.message : "Billing-Sync fehlgeschlagen.");
    }
  });

  const retryProvisioningMutation = useMutation({
    mutationFn: (slug: string) => api.retryTenantProvisioning(slug),
    onMutate: (slug) => {
      setTenantProvisioningError("");
      setTenantProvisioningInfo(`Provisioning fuer ${slug} laeuft...`);
    },
    onSuccess: async (result) => {
      setTenantProvisioningError("");
      setTenantProvisioningInfo(
        `Provisioning fuer ${result.tenant.slug}: ${result.provisioning.message} Livecheck: ${
          result.tenant.liveStatus?.details ?? "noch nicht geprueft"
        }`
      );
      await queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
    onError: (error) => {
      setTenantProvisioningInfo("");
      setTenantProvisioningError(error instanceof Error ? error.message : "Provisioning konnte nicht erneut gestartet werden.");
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (slug: string) => api.deleteTenant(slug),
    onSuccess: async () => {
      setEditError("");
      setEditInfo("Mandant wurde geloescht.");
      if (editingSlug) setEditingSlug(null);
      await queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
    onError: (err) => setEditError(err instanceof Error ? err.message : "Mandant konnte nicht geloescht werden")
  });

  const isFrameworkV2 = typeof document !== "undefined" && document.body.classList.contains("framework-v2");

  useEffect(() => {
    if (!openActionMenuId) return;
    const closeMenu = (event?: MouseEvent) => {
      if (event && isActionMenuEventTarget(event.target)) return;
      setOpenActionMenuId(null);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };
    window.addEventListener("mousedown", closeMenu);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("mousedown", closeMenu);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [openActionMenuId]);

  useEffect(() => {
    if (!openActionMenuId) return;
    const closeMenu = () => setOpenActionMenuId(null);
    window.addEventListener("resize", closeMenu);
    window.addEventListener("scroll", closeMenu, true);
    return () => {
      window.removeEventListener("resize", closeMenu);
      window.removeEventListener("scroll", closeMenu, true);
    };
  }, [openActionMenuId]);

  useActionMenuAutoClose({
    isOpen: !!openActionMenuId,
    isHovered: isActionMenuHovered,
    closeMenu: () => setOpenActionMenuId(null),
    timeoutMs: 3000
  });

  useEffect(() => {
    if (!openActionMenuId) {
      setActionMenuPosition(null);
      setIsActionMenuHovered(false);
    }
  }, [openActionMenuId]);

  const openActionMenuAtTrigger = (menuId: string, trigger: HTMLElement) => {
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 170;
    const menuHeight = 96;
    const margin = 8;
    const left = Math.min(Math.max(margin, rect.right - menuWidth), Math.max(margin, window.innerWidth - menuWidth - margin));
    const belowTop = rect.bottom + 6;
    const aboveTop = rect.top - menuHeight - 6;
    const top = belowTop + menuHeight <= window.innerHeight - margin ? belowTop : Math.max(margin, aboveTop);
    setActionMenuPosition({ left, top });
    setOpenActionMenuId(menuId);
  };

  const openTenantEditForm = (row: TenantRow) => {
    setEditError("");
    setEditInfo("");
    setEditFaciliooDebugError("");
    setEditFaciliooDebugInfo("");
    setShowEditAdminPassword(false);
    setEditingSlug(row.slug);
    setEditForm(mapTenantToEditForm(row));
  };

  const deleteTenantWithConfirmation = (row: TenantRow) => {
    if (row.slug === "default" || isSystemTenantSlug(row.slug)) return;
    const ok = window.confirm(
      `Mandant '${row.name}' (${row.slug}) wirklich loeschen? Dieser Vorgang entfernt auch alle zugehoerigen Daten.`
    );
    if (!ok) return;
    deleteMutation.mutate(row.slug);
  };

  const columns = useMemo(
    () => [
      { key: "slug", label: "Slug" },
      { key: "name", label: "Mandant" },
      {
        key: "contactEmail",
        label: "Kontakt",
        render: (r: TenantRow) => r.profile?.contactEmail ?? "-"
      },
      { key: "status", label: "Status", render: (r: TenantRow) => <span className={`badge ${r.status === "aktiv" ? "aktiv" : "inaktiv"}`}>{r.status}</span> },
      {
        key: "trial",
        label: "Demozeitraum",
        render: (r: TenantRow) => {
          if (!r.trial?.enabled) return "-";
          if (r.trial.expired) return <span className="badge inaktiv">abgelaufen</span>;
          return <span className="badge aktiv">{`aktiv (${r.trial.remainingDays ?? 0} Tage)`}</span>;
        }
      },
      {
        key: "liveStatus",
        label: "Live-Status",
        render: (r: TenantRow) => {
          const ls = r.liveStatus;
          if (!ls) return <span className="badge inaktiv">unbekannt</span>;
          if (ls.live) return <span className="badge aktiv" title={ls.details}>live</span>;
          const partial = ls.dnsOk || ls.tlsOk || ls.httpOk;
          return (
            <span className="badge inaktiv" style={partial ? { borderColor: "#f59e0b", color: "#b45309" } : undefined} title={ls.details}>
              {partial ? "teilweise" : "offline"}
            </span>
          );
        }
      },
      {
        key: "checks",
        label: "Checks",
        render: (r: TenantRow) => {
          const ls = r.liveStatus;
          if (!ls) return "-";
          return `DNS ${ls.dnsOk ? "ok" : "x"} | TLS ${ls.tlsOk ? "ok" : "x"} | HTTP ${ls.httpOk ? "ok" : "x"}`;
        }
      },
      {
        key: "webhookService",
        label: "Webhook-Verarbeitung",
        render: (r: TenantRow) => {
          const ws = r.webhookService;
          if (!ws) return "-";
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div>{renderTenantServiceStateBadge(ws.statusText)}</div>
              <small style={{ color: "var(--text-soft)" }}>
                {`Q:${ws.queueQueued} | P:${ws.queueProcessing} | Done:${ws.queueProcessed}`}
              </small>
              <small style={{ color: "var(--text-soft)" }}>
                {`Letzter Done: ${formatDateTime(ws.lastProcessedAt ?? undefined)}`}
              </small>
            </div>
          );
        }
      },
      {
        key: "faciliooLiveSyncService",
        label: "Facilioo Live-Sync",
        render: (r: TenantRow) => {
          const ls = r.faciliooLiveSyncService;
          if (!ls) return "-";
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div>{renderTenantServiceStateBadge(ls.statusText)}</div>
              <small style={{ color: "var(--text-soft)" }}>
                {"Intervall: taeglich 02:00"}
              </small>
              <small style={{ color: "var(--text-soft)" }}>
                {`Letzter Lauf: ${formatDateTime(ls.lastFinishedAt ?? undefined)}`}
              </small>
            </div>
          );
        }
      },
      {
        key: "createdAt",
        label: "Angelegt",
        render: (r: TenantRow) => formatDateTime(r.createdAt)
      },
      {
        key: "onlineNow",
        label: "Online jetzt",
        width: 140,
        render: (r: TenantRow) => {
          const users = r.onlineUsers ?? 0;
          const sessions = r.onlineSessions ?? 0;
          if (users <= 0) return <span className="badge inaktiv" style={{ whiteSpace: "nowrap" }}>offline</span>;
          return (
            <span className="badge aktiv" style={{ whiteSpace: "nowrap" }} title={`${sessions} aktive Session${sessions === 1 ? "" : "s"}`}>
              {`online (${users})`}
            </span>
          );
        }
      },
      {
        key: "lastLoginAt",
        label: "Letzter Login",
        render: (r: TenantRow) => formatDateTime(r.lastLoginAt)
      },
      {
        key: "actions",
        label: "Aktionen",
        render: (r: TenantRow) => {
          const provisioningPendingForRow = retryProvisioningMutation.isPending && retryProvisioningMutation.variables === r.slug;
          if (!isFrameworkV2) {
            return (
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" onClick={() => openTenantEditForm(r)}>
                  Bearbeiten
                </button>
                <button
                  type="button"
                  disabled={r.slug === "default" || isSystemTenantSlug(r.slug) || retryProvisioningMutation.isPending}
                  onClick={() => retryProvisioningMutation.mutate(r.slug)}
                >
                  {provisioningPendingForRow ? "Provisioning laeuft..." : "Provisioning erneut starten"}
                </button>
                <button
                  type="button"
                  disabled={r.slug === "default" || isSystemTenantSlug(r.slug) || deleteMutation.isPending}
                  onClick={() => deleteTenantWithConfirmation(r)}
                >
                  Loeschen
                </button>
              </div>
            );
          }

          const menuId = `tenant:${r.slug}`;
          return (
            <div className="resolutions-v2-action-wrap platform-v2-action-wrap" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="resolutions-v2-action-trigger"
                aria-label={`Aktionen fuer ${r.name}`}
                aria-expanded={openActionMenuId === menuId}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  if (openActionMenuId === menuId) {
                    setOpenActionMenuId(null);
                    return;
                  }
                  openActionMenuAtTrigger(menuId, e.currentTarget);
                }}
              >
                &#8942;
              </button>
              {openActionMenuId === menuId && actionMenuPosition && typeof document !== "undefined"
                ? createPortal(
                    <div
                      className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed"
                      style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onMouseEnter={() => setIsActionMenuHovered(true)}
                      onMouseLeave={() => setIsActionMenuHovered(false)}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          openTenantEditForm(r);
                          setOpenActionMenuId(null);
                        }}
                      >
                        Bearbeiten
                      </button>
                      <button
                        type="button"
                        disabled={r.slug === "default" || isSystemTenantSlug(r.slug) || retryProvisioningMutation.isPending}
                        onClick={() => {
                          retryProvisioningMutation.mutate(r.slug);
                          setOpenActionMenuId(null);
                        }}
                      >
                        {provisioningPendingForRow ? "Provisioning laeuft..." : "Provisioning erneut starten"}
                      </button>
                      <button
                        type="button"
                        disabled={r.slug === "default" || isSystemTenantSlug(r.slug) || deleteMutation.isPending}
                        onClick={() => {
                          deleteTenantWithConfirmation(r);
                          setOpenActionMenuId(null);
                        }}
                      >
                        Loeschen
                      </button>
                    </div>,
                    document.body
                  )
                : null}
            </div>
          );
        }
      }
    ],
    [actionMenuPosition, deleteMutation.isPending, isFrameworkV2, openActionMenuId, retryProvisioningMutation.isPending]
  );
  const systemColumns = useMemo(
    () =>
      columns.filter(
        (col) =>
          col.key !== "webhookService" &&
          col.key !== "faciliooLiveSyncService"
      ),
    [columns]
  );

  const canCreate =
    form.slug.trim().length >= 3 &&
    form.name.trim().length > 0 &&
    /\S+@\S+\.\S+/.test(form.contactEmail.trim()) &&
    form.adminUsername.trim().length >= 3 &&
    form.adminDisplayName.trim().length > 0 &&
    form.adminPassword.trim().length >= 8;
  const allTenants = data?.data ?? [];
  const systemRows = useMemo(() => allTenants.filter((row) => isSystemTenantSlug(row.slug)), [allTenants]);
  const tenantRows = useMemo(() => allTenants.filter((row) => !isSystemTenantSlug(row.slug)), [allTenants]);

  useEffect(() => {
    const billingTenant = String(searchParams.get("billingTenant") ?? "").trim().toLowerCase();
    const billingReturn = String(searchParams.get("billingReturn") ?? "").trim().toLowerCase();
    if (!billingTenant || allTenants.length === 0) return;
    const tenant = allTenants.find((row) => row.slug === billingTenant);
    if (!tenant) return;
    setCreateOpen(false);
    setEditingSlug(tenant.slug);
    setShowEditAdminPassword(false);
    setEditForm(mapTenantToEditForm(tenant));
    setEditError("");
    setEditInfo(billingReturn === "success" ? "Stripe Checkout abgeschlossen. Bitte Billing neu synchronisieren." : "Stripe Checkout abgebrochen.");
    const next = new URLSearchParams(searchParams);
    next.delete("billingTenant");
    next.delete("billingReturn");
    setSearchParams(next, { replace: true });
  }, [allTenants, searchParams, setSearchParams]);

  const resolveServiceLabel = (key: string) =>
    key === "facilioo_live_sync"
      ? "Facilioo Live-Sync"
      : key === "generated_dispatch_auto_retry"
        ? "Versand-Retry"
        : key === "dispatch_deadline_scheduler"
          ? "Fristen-/Dispatch-Scheduler"
          : key === "platform_health_monitor"
            ? "Health-Monitor"
            : key;

  const formatServiceInterval = (intervalMs?: number) => {
    if (!intervalMs || intervalMs <= 0) return "-";
    if (intervalMs % 60000 === 0) return `${Math.round(intervalMs / 60000)} min`;
    if (intervalMs % 1000 === 0) return `${Math.round(intervalMs / 1000)} s`;
    return `${intervalMs} ms`;
  };

  const formatServiceCheckedAt = (value?: string) => {
    if (!value) return "-";
    const parsed = Date.parse(value);
    if (!Number.isFinite(parsed)) return "-";
    return new Intl.DateTimeFormat("de-DE", {
      timeZone: "Europe/Berlin",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    }).format(new Date(parsed));
  };
  const formatMemoryMb = (value?: number) => {
    if (!Number.isFinite(Number(value))) return "-";
    return `${Number(value).toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MB`;
  };
  const formatUptime = (value?: number) => {
    const totalSeconds = Math.max(0, Math.floor(Number(value) || 0));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours} h ${minutes} min`;
    if (minutes > 0) return `${minutes} min ${seconds} s`;
    return `${seconds} s`;
  };
  const memoryDebug = servicesStatus?.memoryDebug;
  const memoryHistoryAll = memoryDebug?.history ?? [];
  const selectedMemoryRange = MEMORY_RANGE_OPTIONS.find((option) => option.key === memoryRange) ?? MEMORY_RANGE_OPTIONS[2];
  const memoryWindowMs = selectedMemoryRange.minutes * 60_000;
  const memoryWindowStartMs = Date.now() - memoryWindowMs;
  const memoryHistory = memoryHistoryAll.filter((row) => {
    const capturedAtMs = Date.parse(String(row?.capturedAt ?? ""));
    return Number.isFinite(capturedAtMs) && capturedAtMs >= memoryWindowStartMs;
  });
  const memoryHistoryForChart = memoryHistory.length >= 2 ? memoryHistory : memoryHistoryAll;
  const memoryChartWidth = 920;
  const memoryChartHeight = 166;
  const memoryChartPadding = { top: 12, right: 14, bottom: 24, left: 14 };
  const memoryChartInnerWidth = memoryChartWidth - memoryChartPadding.left - memoryChartPadding.right;
  const memoryChartInnerHeight = memoryChartHeight - memoryChartPadding.top - memoryChartPadding.bottom;
  const memoryRssSeries = memoryHistoryForChart.map((row) => Number(row.rssMb || 0));
  const memoryHeapSeries = memoryHistoryForChart.map((row) => Number(row.heapUsedMb || 0));
  const cpuSeries = memoryHistoryForChart.map((row) => Number(row.cpuPercent || 0));
  const memoryCapacityForChart = Math.max(
    1,
    Number(memoryDebug?.thresholdConfig?.capacityMb ?? 0),
    Number(memoryDebug?.systemMemoryMb?.total ?? 0)
  );
  const memoryChartYMax = Math.max(
    1,
    memoryCapacityForChart,
    ...(memoryRssSeries.length > 0 ? memoryRssSeries : [1]),
    ...(memoryHeapSeries.length > 0 ? memoryHeapSeries : [1]),
    Number(memoryDebug?.thresholdMb.warn ?? 0),
    Number(memoryDebug?.thresholdMb.critical ?? 0)
  );
  const memoryXByIndex = (index: number) => {
    if (memoryHistoryForChart.length <= 1) return memoryChartPadding.left;
    const ratio = index / (memoryHistoryForChart.length - 1);
    return memoryChartPadding.left + ratio * memoryChartInnerWidth;
  };
  const memoryYByValue = (value: number) =>
    memoryChartPadding.top + ((memoryChartYMax - Math.max(0, value)) / memoryChartYMax) * memoryChartInnerHeight;
  const buildMemoryLinePoints = (values: number[]) =>
    values
      .map((value, index) => `${memoryXByIndex(index)},${memoryYByValue(value)}`)
      .join(" ");
  const buildMemoryAreaPath = (values: number[]) => {
    if (values.length < 2) return "";
    const firstX = memoryXByIndex(0);
    const lastX = memoryXByIndex(values.length - 1);
    const baselineY = memoryChartPadding.top + memoryChartInnerHeight;
    const segments = values
      .map((value, index) => `${memoryXByIndex(index)} ${memoryYByValue(value)}`)
      .join(" L ");
    return `M ${firstX} ${baselineY} L ${segments} L ${lastX} ${baselineY} Z`;
  };
  const memoryRssPoints = memoryHistoryForChart.length >= 2 ? buildMemoryLinePoints(memoryRssSeries) : "";
  const memoryHeapPoints = memoryHistoryForChart.length >= 2 ? buildMemoryLinePoints(memoryHeapSeries) : "";
  const memoryRssAreaPath = memoryHistoryForChart.length >= 2 ? buildMemoryAreaPath(memoryRssSeries) : "";
  const memoryHeapAreaPath = memoryHistoryForChart.length >= 2 ? buildMemoryAreaPath(memoryHeapSeries) : "";
  const memoryWarnY = memoryDebug ? memoryYByValue(Number(memoryDebug.thresholdMb.warn ?? 0)) : 0;
  const memoryCriticalY = memoryDebug ? memoryYByValue(Number(memoryDebug.thresholdMb.critical ?? 0)) : 0;
  const memoryGridHorizontalCount = 6;
  const memoryGridVerticalCount = 12;
  const memoryGridHorizontalY = Array.from({ length: memoryGridHorizontalCount + 1 }, (_entry, idx) => {
    const ratio = idx / memoryGridHorizontalCount;
    return memoryChartPadding.top + ratio * memoryChartInnerHeight;
  });
  const memoryGridVerticalX = Array.from({ length: memoryGridVerticalCount + 1 }, (_entry, idx) => {
    const ratio = idx / memoryGridVerticalCount;
    return memoryChartPadding.left + ratio * memoryChartInnerWidth;
  });
  const memoryYAxisTicks = Array.from({ length: 5 }, (_entry, idx) => {
    const ratio = idx / 4;
    const value = memoryChartYMax * (1 - ratio);
    return {
      y: memoryChartPadding.top + ratio * memoryChartInnerHeight,
      label: `${Math.round(value)}`
    };
  });
  const memorySpikeCallouts = memoryHistoryForChart
    .map((row, index) => ({ index, value: Number(row.rssMb || 0) }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 2)
    .sort((a, b) => a.index - b.index);
  const memoryTickIndexesRaw = [0, memoryHistoryForChart.length - 1].filter((value) => value >= 0);
  const memoryTickIndexes = Array.from(new Set(memoryTickIndexesRaw));
  const cpuChartHeight = 72;
  const cpuChartYMax = Math.max(10, ...cpuSeries, 100);
  const cpuChartInnerHeight = cpuChartHeight - memoryChartPadding.top - 12;
  const cpuGridHorizontalCount = 4;
  const cpuGridVerticalCount = 12;
  const cpuGridHorizontalY = Array.from({ length: cpuGridHorizontalCount + 1 }, (_entry, idx) => {
    const ratio = idx / cpuGridHorizontalCount;
    return memoryChartPadding.top + ratio * cpuChartInnerHeight;
  });
  const cpuGridVerticalX = Array.from({ length: cpuGridVerticalCount + 1 }, (_entry, idx) => {
    const ratio = idx / cpuGridVerticalCount;
    return memoryChartPadding.left + ratio * memoryChartInnerWidth;
  });
  const cpuYByValue = (value: number) =>
    memoryChartPadding.top + ((cpuChartYMax - Math.max(0, value)) / cpuChartYMax) * cpuChartInnerHeight;
  const cpuPoints =
    memoryHistoryForChart.length >= 2
      ? cpuSeries
          .map((value, index) => `${memoryXByIndex(index)},${cpuYByValue(value)}`)
          .join(" ")
      : "";
  const formatMemoryTimeTick = (value?: string) => {
    if (!value) return "-";
    const parsed = Date.parse(value);
    if (!Number.isFinite(parsed)) return "-";
    return new Intl.DateTimeFormat("de-DE", {
      timeZone: "Europe/Berlin",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    }).format(new Date(parsed));
  };
  const formatMemoryDateTimeBerlin = (value?: string) => {
    if (!value) return "-";
    const parsed = Date.parse(value);
    if (!Number.isFinite(parsed)) return "-";
    return new Intl.DateTimeFormat("de-DE", {
      timeZone: "Europe/Berlin",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    }).format(new Date(parsed));
  };
  const resolveMemoryHoverIndex = (event: ReactMouseEvent<SVGSVGElement>) => {
    if (memoryHistoryForChart.length < 2) {
      setMemoryHoverIndex(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    if (!Number.isFinite(rect.width) || rect.width <= 0) {
      setMemoryHoverIndex(null);
      return;
    }
    const relativeClientX = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
    const chartX = (relativeClientX / rect.width) * memoryChartWidth;
    const clampedX = Math.max(memoryChartPadding.left, Math.min(memoryChartPadding.left + memoryChartInnerWidth, chartX));
    const ratio = (clampedX - memoryChartPadding.left) / Math.max(1, memoryChartInnerWidth);
    const index = Math.round(ratio * (memoryHistoryForChart.length - 1));
    setMemoryHoverIndex(Math.max(0, Math.min(memoryHistoryForChart.length - 1, index)));
  };
  const hoveredMemoryRow =
    memoryHoverIndex !== null && memoryHoverIndex >= 0 && memoryHoverIndex < memoryHistoryForChart.length
      ? memoryHistoryForChart[memoryHoverIndex]
      : null;
  const hoveredMemoryX = memoryHoverIndex !== null ? memoryXByIndex(memoryHoverIndex) : null;
  const hoveredMemoryRss = hoveredMemoryRow ? Number(hoveredMemoryRow.rssMb || 0) : null;
  const hoveredMemoryHeap = hoveredMemoryRow ? Number(hoveredMemoryRow.heapUsedMb || 0) : null;
  const hoveredMemoryCpu = hoveredMemoryRow ? Number(hoveredMemoryRow.cpuPercent || 0) : null;
  const memoryTooltipWidth = 242;
  const memoryTooltipHeight = 58;
  const memoryTooltipX =
    hoveredMemoryX === null
      ? memoryChartPadding.left + 8
      : Math.max(
          memoryChartPadding.left + 8,
          Math.min(memoryChartPadding.left + memoryChartInnerWidth - memoryTooltipWidth - 8, hoveredMemoryX - memoryTooltipWidth / 2)
        );
  const memoryHistoryPeakRss = memoryHistoryForChart.length
    ? Math.max(...memoryHistoryForChart.map((row) => Number(row.rssMb || 0)))
    : null;
  const memoryHistoryMinRss = memoryHistoryForChart.length
    ? Math.min(...memoryHistoryForChart.map((row) => Number(row.rssMb || 0)))
    : null;
  const cpuPeak = memoryHistoryForChart.length ? Math.max(...memoryHistoryForChart.map((row) => Number(row.cpuPercent || 0))) : null;
  const visibleServices = useMemo(
    () => (servicesStatus?.services ?? []).filter((service) => service.service !== "facilioo_live_sync"),
    [servicesStatus]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(MEMORY_RANGE_STORAGE_KEY, memoryRange);
  }, [memoryRange]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(MEMORY_DEBUG_ENABLED_STORAGE_KEY, memoryDebugEnabled ? "true" : "false");
  }, [memoryDebugEnabled]);

  return (
    <div className={isFrameworkV2 ? "platform-v2-page resolutions-v2-page" : undefined}>
      <section className={`page-header panel ${isFrameworkV2 ? "resolutions-v2-page-header platform-v2-page-header" : ""}`}>
        <h2 className={isFrameworkV2 ? "resolutions-v2-title" : ""} style={{ margin: 0 }}>
          {isFrameworkV2 ? (
            <span className="resolutions-v2-title-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M4.8 7.6h14.4v11H4.8z" stroke="currentColor" strokeWidth="1.8" />
                <path d="M8 4.9h8v2.7H8zM8.1 11h2.5M13.4 11h2.5M8.1 14.4h2.5M13.4 14.4h2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </span>
          ) : null}
          {isFrameworkV2 ? (
            <span className="resolutions-v2-title-copy">
              <span className="resolutions-v2-title-main">SaaS-Mandanten</span>
              <span className="resolutions-v2-title-sub">Plattformverwaltung, Dienste und Mandanten-Onboarding</span>
            </span>
          ) : (
            <span>SaaS-Mandanten (Plattform)</span>
          )}
        </h2>
        <button
          type="button"
          className={isFrameworkV2 ? "resolutions-v2-create-btn" : ""}
          onClick={() => {
            setEditingSlug(null);
            setCreateOpen((prev) => !prev);
            setCreateError("");
            setEditError("");
            setEditInfo("");
          }}
        >
          {createOpen ? "Schließen" : "Neuer Mandant"}
        </button>
      </section>

      <section className={`panel ${isFrameworkV2 ? "platform-v2-services-panel" : ""}`}>
        <div className={isFrameworkV2 ? "platform-v2-section-head" : ""}>
          <h3 style={{ marginTop: 0 }}>Dienste</h3>
        </div>
        <div className={isFrameworkV2 ? "platform-v2-message-row" : ""} style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 10 }}>
          {serviceActionInfo ? <span className="info-text">{serviceActionInfo}</span> : null}
          {serviceActionError ? <span className="error-text">{serviceActionError}</span> : null}
        </div>
        {servicesIsError ? (
          <div className="error-text">{servicesError instanceof Error ? servicesError.message : "Service-Status konnte nicht geladen werden."}</div>
        ) : servicesLoading ? (
          <div>Lade Service-Status...</div>
        ) : (
          <div className={isFrameworkV2 ? "platform-v2-service-grid" : ""} style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {visibleServices.map((service) => {
              const label = resolveServiceLabel(service.service);
              const badgeClass = service.statusText === "ok" ? "aktiv" : service.statusText === "nicht_ok" ? "inaktiv" : "";
              const badgeStyle = service.statusText === "inaktiv" ? { borderColor: "#94a3b8", color: "#64748b" } : undefined;
              const statusLabel = service.statusText === "ok" ? "ok" : service.statusText === "nicht_ok" ? "nicht ok" : "inaktiv";
              const monitorIssues = service.service === "platform_health_monitor" ? service.issues ?? [] : [];
              const monitorIssueCount = service.service === "platform_health_monitor" ? service.issueCount ?? monitorIssues.length : 0;
              const showMonitorDetails = service.service === "platform_health_monitor" && service.statusText === "nicht_ok";
              return (
                <div key={service.service} className={`panel ${isFrameworkV2 ? "platform-v2-service-card" : ""}`} style={{ minWidth: 240, padding: 10 }}>
                  <div className={isFrameworkV2 ? "platform-v2-card-title" : ""} style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
                  <span className={`badge ${badgeClass}`} style={{ ...badgeStyle, whiteSpace: "nowrap" }}>
                    {statusLabel}
                  </span>
                  {showMonitorDetails ? (
                    <div style={{ marginTop: 8, display: "grid", gap: 4 }}>
                      {service.lastError ? (
                        <small style={{ color: "#b42318", display: "block" }}>{`Fehler: ${service.lastError}`}</small>
                      ) : null}
                      {service.lastNotifyError ? (
                        <small style={{ color: "#b54708", display: "block" }}>{`Benachrichtigung: ${service.lastNotifyError}`}</small>
                      ) : null}
                      {monitorIssues.map((issue, index) => (
                        <small key={`${service.service}-issue-${index}`} style={{ color: "var(--text-soft)", display: "block" }}>
                          {issue}
                        </small>
                      ))}
                      {service.hasMoreIssues ? (
                        <small style={{ color: "var(--text-soft)", display: "block" }}>
                          {`Weitere ${Math.max(0, monitorIssueCount - monitorIssues.length)} Issue(s) vorhanden.`}
                        </small>
                      ) : null}
                      {!service.lastError && !service.lastNotifyError && monitorIssues.length === 0 ? (
                        <small style={{ color: "var(--text-soft)", display: "block" }}>
                          Ursache wird ermittelt. Bitte "Jetzt prüfen" ausführen.
                        </small>
                      ) : null}
                    </div>
                  ) : null}
                  <div className={isFrameworkV2 ? "platform-v2-card-actions" : ""} style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={() => {
                        setServiceActionInfo("");
                        setServiceActionError("");
                        checkServiceMutation.mutate(service.service as PlatformServiceKey);
                      }}
                      disabled={checkServiceMutation.isPending || notifyServiceMutation.isPending}
                    >
                      Jetzt prüfen
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setServiceActionInfo("");
                        setServiceActionError("");
                        notifyServiceMutation.mutate({ service: service.service as PlatformServiceKey });
                      }}
                      disabled={checkServiceMutation.isPending || notifyServiceMutation.isPending}
                    >
                      Störungsmeldung senden
                    </button>
                  </div>
                </div>
              );
            })}
            {servicesStatus && visibleServices.length === 0 ? <div>Keine Dienste konfiguriert.</div> : null}
          </div>
        )}
        <div className={`panel ${isFrameworkV2 ? "platform-v2-debug-card" : ""}`} style={{ marginTop: 12, padding: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "space-between" }}>
              <strong>RAM/CPU-Log</strong>
              <button
                type="button"
                onClick={() => setMemoryDebugEnabled((prev) => !prev)}
                aria-pressed={memoryDebugEnabled}
                aria-label={`RAM/CPU-Log ${memoryDebugEnabled ? "deaktivieren" : "aktivieren"}`}
                title={`RAM/CPU-Log ist ${memoryDebugEnabled ? "aktiv" : "aus"}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: 0
                }}
              >
                <span
                  style={{
                    position: "relative",
                    width: 54,
                    height: 30,
                    borderRadius: 999,
                    background: memoryDebugEnabled ? "#16a34a" : "#94a3b8",
                    transition: "background 0.2s ease"
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      top: 3,
                      left: memoryDebugEnabled ? 27 : 3,
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: "#ffffff",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                      transition: "left 0.2s ease"
                    }}
                  />
                </span>
                <span className={`badge ${memoryDebugEnabled ? "aktiv" : "inaktiv"}`}>
                  {memoryDebugEnabled ? "aktiv" : "aus"}
                </span>
              </button>
            </div>
          <div style={{ marginTop: 6, color: "var(--text-soft)" }}>
            {memoryDebugEnabled
              ? "RAM/CPU-Log aktiv: Verlauf wird live aktualisiert."
              : "RAM/CPU-Log aus: keine Live-Aktualisierung und keine RAM/CPU-Sammlung über den Status-Endpunkt."}
          </div>
        </div>
        {memoryDebugEnabled && memoryDebug ? (
          <div className={`panel ${isFrameworkV2 ? "platform-v2-debug-card platform-v2-memory-card" : ""}`} style={{ marginTop: 12, padding: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <strong>RAM-Debug</strong>
              <span
                className={`badge ${memoryDebug.statusText === "ok" ? "aktiv" : "inaktiv"}`}
                style={
                  memoryDebug.statusText === "warnung"
                    ? { borderColor: "#f59e0b", color: "#b45309" }
                    : memoryDebug.statusText === "kritisch"
                      ? { borderColor: "#dc2626", color: "#dc2626" }
                      : undefined
                }
              >
                {memoryDebug.statusText}
              </span>
            </div>
            <div style={{ marginTop: 8, color: "var(--text-soft)" }}>
              {`RSS ${formatMemoryMb(memoryDebug.current.rssMb)} | Heap ${formatMemoryMb(memoryDebug.current.heapUsedMb)} / ${formatMemoryMb(memoryDebug.current.heapTotalMb)} | CPU ${Number(memoryDebug.current.cpuPercent ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}% (gesamt ${Number(memoryDebug.current.cpuPercentTotal ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}% | system-normalisiert ${(
                Number(memoryDebug.current.cpuPercentTotal ?? 0) / Math.max(1, Number(memoryDebug.cpuConfig?.systemCores ?? memoryDebug.cpuCores ?? 1))
              ).toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%) | Kerne effektiv ${memoryDebug.cpuConfig?.effectiveCores ?? memoryDebug.cpuCores} / System ${memoryDebug.cpuConfig?.systemCores ?? memoryDebug.cpuCores} | Uptime ${formatUptime(memoryDebug.uptimeSeconds)}`}
            </div>
            <div style={{ marginTop: 4, color: "var(--text-soft)" }}>
              {`Server-RAM: Gesamt ${formatMemoryMb(memoryDebug.systemMemoryMb?.total)}, frei ${formatMemoryMb(memoryDebug.systemMemoryMb?.free)}, belegt ${formatMemoryMb(memoryDebug.systemMemoryMb?.used)} | Prozessanteil RSS ${Number(memoryDebug.processShareOfSystemPercent ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
            </div>
            <div style={{ marginTop: 4, color: "var(--text-soft)" }}>
              {`Grenzen: Warnung ab ${formatMemoryMb(memoryDebug.thresholdMb.warn)}, kritisch ab ${formatMemoryMb(memoryDebug.thresholdMb.critical)}.`}
            </div>
            <div style={{ marginTop: 4, color: "var(--text-soft)" }}>
              {`Basis: ${
                memoryDebug.thresholdConfig?.capacitySource === "env_override"
                  ? "ENV-Kapazität"
                  : memoryDebug.thresholdConfig?.capacitySource === "cgroup"
                    ? "cgroup-Limit"
                    : "System-RAM"
              } ${formatMemoryMb(memoryDebug.thresholdConfig?.capacityMb)} | System ${formatMemoryMb(memoryDebug.thresholdConfig?.systemCapacityMb)}${
                memoryDebug.thresholdConfig?.cgroupCapacityMb
                  ? ` | cgroup roh ${formatMemoryMb(memoryDebug.thresholdConfig?.cgroupCapacityMb)}`
                  : ""
              } | Reserve ${formatMemoryMb(memoryDebug.thresholdConfig?.reservedMb)} | Budget ${formatMemoryMb(memoryDebug.thresholdConfig?.budgetMb)} | Modus ${
                memoryDebug.thresholdConfig?.source === "env_override" ? "ENV Override" : "dynamisch"
              }`}
            </div>
            <div style={{ marginTop: 2, color: "var(--text-soft)" }}>
              {`CPU-Basis: ${
                memoryDebug.cpuConfig?.source === "env_override"
                  ? "ENV-Kerne"
                  : memoryDebug.cpuConfig?.source === "cgroup"
                    ? "cgroup-Limit"
                    : "System-Kerne"
              } | effektiv ${memoryDebug.cpuConfig?.effectiveCores ?? memoryDebug.cpuCores} | System ${memoryDebug.cpuConfig?.systemCores ?? memoryDebug.cpuCores}${
                memoryDebug.cpuConfig?.availableParallelismCores
                  ? ` | availableParallelism ${memoryDebug.cpuConfig.availableParallelismCores}`
                  : ""
              }${
                memoryDebug.cpuConfig?.cgroupQuotaCores
                  ? ` | cgroup quota ${memoryDebug.cpuConfig.cgroupQuotaCores}`
                  : ""
              }${
                memoryDebug.cpuConfig?.cgroupCpusetCores
                  ? ` | cgroup cpuset ${memoryDebug.cpuConfig.cgroupCpusetCores}`
                  : ""
              }`}
            </div>
            {memoryDebug.thresholdConfig?.source === "dynamic" ? (
              <div style={{ marginTop: 2, color: "var(--text-soft)" }}>
                {`Dynamik: Reserve ${(Number(memoryDebug.thresholdConfig?.reserveFraction ?? 0) * 100).toLocaleString("de-DE", { maximumFractionDigits: 0 })}% | Warnung ${(Number(memoryDebug.thresholdConfig?.warnFractionOfBudget ?? 0) * 100).toLocaleString("de-DE", { maximumFractionDigits: 0 })}% vom Budget | Kritisch ${(Number(memoryDebug.thresholdConfig?.criticalFractionOfBudget ?? 0) * 100).toLocaleString("de-DE", { maximumFractionDigits: 0 })}% vom Budget`}
              </div>
            ) : null}
            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {MEMORY_RANGE_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={`badge ${memoryRange === option.key ? "aktiv" : "inaktiv"}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setMemoryRange(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {memoryHistoryForChart.length >= 2 ? (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                  <span className="badge inaktiv" style={{ borderColor: "#99f6e4", color: "#0f766e", background: "#f0fdfa" }}>
                    {`Min RSS ${formatMemoryMb(memoryHistoryMinRss ?? undefined)}`}
                  </span>
                  <span className="badge inaktiv" style={{ borderColor: "#bfdbfe", color: "#1d4ed8", background: "#eff6ff" }}>
                    {`Max RSS ${formatMemoryMb(memoryHistoryPeakRss ?? undefined)}`}
                  </span>
                  <span className="badge inaktiv" style={{ borderColor: "#ddd6fe", color: "#6d28d9", background: "#f5f3ff" }}>
                    {`CPU-Peak ${Number(cpuPeak ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
                  </span>
                  <span className="badge inaktiv" style={{ borderColor: "#e2e8f0", color: "#334155", background: "#f8fafc" }}>
                    {`Letzte Messung ${formatServiceCheckedAt(memoryDebug.current.capturedAt)}`}
                  </span>
                </div>
                <svg
                  viewBox={`0 0 ${memoryChartWidth} ${memoryChartHeight}`}
                  style={{ width: "100%", height: "auto", display: "block" }}
                  onMouseMove={resolveMemoryHoverIndex}
                  onMouseLeave={() => setMemoryHoverIndex(null)}
                >
                  <defs>
                    <linearGradient id="memoryChartBg" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#f8fbff" />
                      <stop offset="100%" stopColor="#f1f5f9" />
                    </linearGradient>
                    <radialGradient id="memoryChartGlow" cx="50%" cy="0%" r="80%">
                      <stop offset="0%" stopColor="#93c5fd" stopOpacity="0.22" />
                      <stop offset="100%" stopColor="#93c5fd" stopOpacity="0" />
                    </radialGradient>
                    <linearGradient id="memoryRssFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0f766e" stopOpacity="0.28" />
                      <stop offset="100%" stopColor="#0f766e" stopOpacity="0.02" />
                    </linearGradient>
                    <linearGradient id="memoryHeapFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563eb" stopOpacity="0.18" />
                      <stop offset="100%" stopColor="#2563eb" stopOpacity="0.02" />
                    </linearGradient>
                    <filter id="memoryRssGlow" x="-20%" y="-30%" width="140%" height="160%">
                      <feGaussianBlur stdDeviation="1.15" result="blur" />
                      <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>
                  <rect
                    x={memoryChartPadding.left}
                    y={memoryChartPadding.top}
                    width={memoryChartInnerWidth}
                    height={memoryChartInnerHeight}
                    fill="url(#memoryChartBg)"
                    stroke="#cbd5e1"
                    rx="8"
                  />
                  <rect
                    x={memoryChartPadding.left}
                    y={memoryChartPadding.top}
                    width={memoryChartInnerWidth}
                    height={memoryChartInnerHeight}
                    fill="url(#memoryChartGlow)"
                    rx="8"
                  />
                  {memoryGridHorizontalY.map((y, index) => (
                    <line
                      key={`memory-grid-h-${index}`}
                      x1={memoryChartPadding.left}
                      x2={memoryChartPadding.left + memoryChartInnerWidth}
                      y1={y}
                      y2={y}
                      stroke={index === memoryGridHorizontalY.length - 1 ? "#cbd5e1" : "#dbeafe"}
                      strokeDasharray={index % 2 === 0 ? "2 4" : "0"}
                      strokeWidth="0.8"
                    />
                  ))}
                  {memoryGridVerticalX.map((x, index) => (
                    <line
                      key={`memory-grid-v-${index}`}
                      x1={x}
                      x2={x}
                      y1={memoryChartPadding.top}
                      y2={memoryChartPadding.top + memoryChartInnerHeight}
                      stroke="#e2e8f0"
                      strokeDasharray="2 4"
                      strokeWidth="0.8"
                      opacity={index % 2 === 0 ? 0.9 : 0.55}
                    />
                  ))}
                  {memoryYAxisTicks.map((tick, index) => (
                    <text key={`memory-y-tick-${index}`} x={memoryChartPadding.left + 6} y={tick.y - 2} fontSize="8.5" fill="#64748b">
                      {`${tick.label} MB`}
                    </text>
                  ))}
                  {memoryHistoryForChart.length >= 2 ? <path d={memoryRssAreaPath} fill="url(#memoryRssFill)" /> : null}
                  {memoryHistoryForChart.length >= 2 ? <path d={memoryHeapAreaPath} fill="url(#memoryHeapFill)" /> : null}
                  <line
                    x1={memoryChartPadding.left}
                    x2={memoryChartPadding.left + memoryChartInnerWidth}
                    y1={memoryWarnY}
                    y2={memoryWarnY}
                    stroke="#f59e0b"
                    strokeDasharray="4 4"
                    strokeWidth="0.9"
                  />
                  <line
                    x1={memoryChartPadding.left}
                    x2={memoryChartPadding.left + memoryChartInnerWidth}
                    y1={memoryCriticalY}
                    y2={memoryCriticalY}
                    stroke="#dc2626"
                    strokeDasharray="5 4"
                    strokeWidth="0.9"
                  />
                  <polyline points={memoryHeapPoints} fill="none" stroke="#2563eb" strokeWidth="1.15" strokeLinejoin="round" strokeLinecap="round" />
                  <polyline
                    points={memoryRssPoints}
                    fill="none"
                    stroke="#0f766e"
                    strokeWidth="1.1"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    filter="url(#memoryRssGlow)"
                  />
                  {hoveredMemoryRow && hoveredMemoryX !== null ? (
                    <g pointerEvents="none">
                      <line
                        x1={hoveredMemoryX}
                        x2={hoveredMemoryX}
                        y1={memoryChartPadding.top}
                        y2={memoryChartPadding.top + memoryChartInnerHeight}
                        stroke="#0f172a"
                        strokeOpacity="0.22"
                        strokeDasharray="3 4"
                        strokeWidth="0.85"
                      />
                      <circle
                        cx={hoveredMemoryX}
                        cy={memoryYByValue(hoveredMemoryRss ?? 0)}
                        r="2.6"
                        fill="#0f766e"
                        stroke="#ffffff"
                        strokeWidth="1"
                      />
                      <circle
                        cx={hoveredMemoryX}
                        cy={memoryYByValue(hoveredMemoryHeap ?? 0)}
                        r="2.5"
                        fill="#2563eb"
                        stroke="#ffffff"
                        strokeWidth="1"
                      />
                      <rect
                        x={memoryTooltipX}
                        y={memoryChartPadding.top + 6}
                        width={memoryTooltipWidth}
                        height={memoryTooltipHeight}
                        rx="8"
                        fill="#ffffff"
                        stroke="#cbd5e1"
                        strokeWidth="0.9"
                        opacity="0.98"
                      />
                      <text x={memoryTooltipX + 8} y={memoryChartPadding.top + 19} fontSize="8.8" fill="#0f172a" fontWeight={700}>
                        {formatMemoryDateTimeBerlin(hoveredMemoryRow.capturedAt)}
                      </text>
                      <text x={memoryTooltipX + 8} y={memoryChartPadding.top + 33} fontSize="8.5" fill="#0f766e">
                        {`RSS ${formatMemoryMb(hoveredMemoryRss ?? undefined)}`}
                      </text>
                      <text x={memoryTooltipX + 95} y={memoryChartPadding.top + 33} fontSize="8.5" fill="#2563eb">
                        {`Heap ${formatMemoryMb(hoveredMemoryHeap ?? undefined)}`}
                      </text>
                      <text x={memoryTooltipX + 8} y={memoryChartPadding.top + 47} fontSize="8.5" fill="#6d28d9">
                        {`CPU ${Number(hoveredMemoryCpu ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
                      </text>
                    </g>
                  ) : null}
                  {memorySpikeCallouts.map((spike, index) => {
                    const x = memoryXByIndex(spike.index);
                    const y = memoryYByValue(spike.value);
                    const labelY = Math.max(memoryChartPadding.top + 20, y - 12 - index * 16);
                    const labelX = Math.max(
                      memoryChartPadding.left + 34,
                      Math.min(memoryChartPadding.left + memoryChartInnerWidth - 34, x)
                    );
                    return (
                      <g key={`memory-spike-${spike.index}`}>
                        <circle cx={x} cy={y} r="3.5" fill="#0f766e" stroke="#ffffff" strokeWidth="1.4" />
                        <line x1={x} x2={labelX} y1={y} y2={labelY - 8} stroke="#0f766e" strokeDasharray="2 3" strokeWidth="1" />
                        <rect x={labelX - 33} y={labelY - 18} width="66" height="14" rx="5" fill="#ecfeff" opacity="0.95" />
                        <text x={labelX} y={labelY - 8} textAnchor="middle" fontSize="8.5" fill="#0f766e" fontWeight={600}>
                          {`Spike ${Math.round(spike.value)} MB`}
                        </text>
                      </g>
                    );
                  })}
                  {memoryTickIndexes.map((index) => {
                    const x = memoryXByIndex(index);
                    const label = formatMemoryTimeTick(memoryHistoryForChart[index]?.capturedAt);
                    const isFirst = index === 0;
                    const isLast = index === memoryHistoryForChart.length - 1;
                    const textAnchor = isFirst ? "start" : isLast ? "end" : "middle";
                    const labelX = isFirst ? x + 1 : isLast ? x - 1 : x;
                    return (
                      <g key={`memory-tick-${index}`}>
                        <line
                          x1={x}
                          x2={x}
                          y1={memoryChartPadding.top + memoryChartInnerHeight}
                          y2={memoryChartPadding.top + memoryChartInnerHeight + 5}
                          stroke="#64748b"
                        />
                        <text
                          x={labelX}
                          y={memoryChartPadding.top + memoryChartInnerHeight + 16}
                          textAnchor={textAnchor}
                          fontSize="9"
                          fill="#64748b"
                        >
                          {label}
                        </text>
                      </g>
                    );
                  })}
                </svg>
                {memoryHistoryForChart.length >= 2 ? (
                  <svg
                    viewBox={`0 0 ${memoryChartWidth} ${cpuChartHeight}`}
                    style={{ width: "100%", height: "auto", display: "block", marginTop: 4 }}
                    onMouseMove={resolveMemoryHoverIndex}
                    onMouseLeave={() => setMemoryHoverIndex(null)}
                  >
                    <rect
                      x={memoryChartPadding.left}
                      y={memoryChartPadding.top}
                      width={memoryChartInnerWidth}
                      height={cpuChartInnerHeight}
                      fill="url(#memoryChartBg)"
                      stroke="#cbd5e1"
                      rx="8"
                    />
                    {cpuGridHorizontalY.map((y, index) => (
                      <line
                        key={`cpu-grid-h-${index}`}
                        x1={memoryChartPadding.left}
                        x2={memoryChartPadding.left + memoryChartInnerWidth}
                        y1={y}
                        y2={y}
                        stroke="#ede9fe"
                        strokeDasharray="2 4"
                        strokeWidth="0.8"
                      />
                    ))}
                    {cpuGridVerticalX.map((x, index) => (
                      <line
                        key={`cpu-grid-v-${index}`}
                        x1={x}
                        x2={x}
                        y1={memoryChartPadding.top}
                        y2={memoryChartPadding.top + cpuChartInnerHeight}
                        stroke="#e2e8f0"
                        strokeDasharray="2 4"
                        strokeWidth="0.8"
                        opacity={index % 2 === 0 ? 0.85 : 0.5}
                      />
                    ))}
                    <polyline points={cpuPoints} fill="none" stroke="#7c3aed" strokeWidth="1.1" strokeLinejoin="round" strokeLinecap="round" />
                    <text x={memoryChartPadding.left + 8} y={memoryChartPadding.top + 10} textAnchor="start" fontSize="9" fill="#6d28d9">
                      {`CPU ${Math.round(cpuChartYMax)}% max`}
                    </text>
                    <text x={memoryChartPadding.left + memoryChartInnerWidth - 8} y={memoryChartPadding.top + 10} textAnchor="end" fontSize="8.5" fill="#7c3aed">
                      100%
                    </text>
                    <text
                      x={memoryChartPadding.left + memoryChartInnerWidth - 8}
                      y={memoryChartPadding.top + cpuChartInnerHeight - 2}
                      textAnchor="end"
                      fontSize="8.5"
                      fill="#7c3aed"
                    >
                      0%
                    </text>
                  </svg>
                ) : null}
                <div style={{ display: "flex", gap: 12, color: "var(--text-soft)", fontSize: 12, marginTop: 4, flexWrap: "wrap" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 99, background: "#0f766e", display: "inline-block" }} />
                    RSS
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 99, background: "#2563eb", display: "inline-block" }} />
                    Heap genutzt
                  </span>
                  <span style={{ color: "#b45309" }}>{`Warnung ab ${formatMemoryMb(memoryDebug.thresholdMb.warn)}`}</span>
                  <span style={{ color: "#b91c1c" }}>{`Kritisch ab ${formatMemoryMb(memoryDebug.thresholdMb.critical)}`}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 99, background: "#7c3aed", display: "inline-block" }} />
                    CPU
                  </span>
                </div>
              </div>
            ) : null}
            <div style={{ marginTop: 4, color: "var(--text-soft)" }}>
              {`Verlauf (${selectedMemoryRange.label}, ${memoryHistoryForChart.length} Messungen): Min RSS ${formatMemoryMb(memoryHistoryMinRss ?? undefined)}, Max RSS ${formatMemoryMb(memoryHistoryPeakRss ?? undefined)}, CPU-Peak ${Number(cpuPeak ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%, letzte Messung ${formatServiceCheckedAt(memoryDebug.current.capturedAt)}.`}
            </div>
            <div style={{ marginTop: 2, color: "var(--text-soft)" }}>
              {`Skalierung: 100% = ${formatMemoryMb(memoryChartYMax)}.`}
            </div>
          </div>
        ) : null}
        {visibleServices.length > 0 ? (
          <div className={`table-wrap ${isFrameworkV2 ? "platform-v2-interval-table" : ""}`} style={{ marginTop: 12 }}>
            <h4 style={{ marginTop: 0 }}>Prüfintervalle</h4>
            <table>
              <thead>
                <tr>
                  <th>Dienst</th>
                  <th>Intervall</th>
                  <th>Letzter Test</th>
                </tr>
              </thead>
              <tbody>
                {visibleServices.map((service) => (
                  <tr key={`${service.service}-interval`}>
                    <td>{resolveServiceLabel(service.service)}</td>
                    <td>{formatServiceInterval(service.intervalMs)}</td>
                    <td>{formatServiceCheckedAt((service as { checkedAt?: string | null }).checkedAt ?? undefined)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {createOpen && !editingSlug ? (
        <section className={`panel create-form ${isFrameworkV2 ? "platform-v2-form-panel" : ""}`}>
          <h3 style={{ marginTop: 0 }}>Mandant onboarden</h3>
          <p className="tenant-onboarding-intro">
            Dieser Bereich ist von der normalen Mandantenverwaltung getrennt und nur für Plattform-Admins gedacht.
          </p>
          <div className="tenant-onboarding-sections">
          <div className="tenant-onboarding-section">
            <h4>Mandant</h4>
            <div className="tenant-onboarding-grid">
              <div className="form-field">
                <label>Mandanten-Slug* (Subdomain)</label>
                <input
                  placeholder="z. B. hausverwaltung-muster"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
                />
              </div>
              <div className="form-field">
                <label>Mandantenname*</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Rechtsform</label>
                <input value={form.legalForm} onChange={(e) => setForm({ ...form, legalForm: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Website</label>
                <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="tenant-onboarding-section">
            <h4>Kontakt und Adresse</h4>
            <div className="tenant-onboarding-grid">
              <div className="form-field">
                <label>Kontakt-E-Mail*</label>
                <input value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Kontakt-Telefon</label>
                <input value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Straße</label>
                <input value={form.addressStreet} onChange={(e) => setForm({ ...form, addressStreet: e.target.value })} />
              </div>
              <div className="form-field">
                <label>PLZ</label>
                <input value={form.addressPostalCode} onChange={(e) => setForm({ ...form, addressPostalCode: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Ort</label>
                <input value={form.addressCity} onChange={(e) => setForm({ ...form, addressCity: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Land</label>
                <input value={form.addressCountry} onChange={(e) => setForm({ ...form, addressCountry: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="tenant-onboarding-section">
            <h4>Ansprechpartner</h4>
            <div className="tenant-onboarding-grid">
              <div className="form-field">
                <label>Anrede</label>
                <input value={form.contactSalutation} onChange={(e) => setForm({ ...form, contactSalutation: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Titel</label>
                <input value={form.contactTitle} onChange={(e) => setForm({ ...form, contactTitle: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Vorname</label>
                <input value={form.contactFirstName} onChange={(e) => setForm({ ...form, contactFirstName: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Nachname</label>
                <input value={form.contactLastName} onChange={(e) => setForm({ ...form, contactLastName: e.target.value })} />
              </div>
              <div className="form-field">
                <label>E-Mail</label>
                <input value={form.contactPersonEmail} onChange={(e) => setForm({ ...form, contactPersonEmail: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Telefon</label>
                <input value={form.contactPersonPhone} onChange={(e) => setForm({ ...form, contactPersonPhone: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="tenant-onboarding-section">
            <h4>Standards und Technik</h4>
            <div className="tenant-onboarding-grid">
              <div className="form-field">
                <label>Zeitzone</label>
                <input value={form.technicalTimezone} onChange={(e) => setForm({ ...form, technicalTimezone: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Locale</label>
                <input value={form.technicalLocale} onChange={(e) => setForm({ ...form, technicalLocale: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Währung</label>
                <input value={form.technicalCurrency} onChange={(e) => setForm({ ...form, technicalCurrency: e.target.value })} />
              </div>
              <div className="form-field">
                <label>UI-Framework</label>
                <select
                  value={form.technicalUiFramework}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      technicalUiFramework: e.target.value === "mockup_v2" ? "mockup_v2" : "classic"
                    })
                  }
                >
                  <option value="classic">Classic (bisherig)</option>
                  <option value="mockup_v2">Mockup V2 (neu)</option>
                </select>
              </div>
              <div className="form-field">
                <label>Standard Stimmrechtsart</label>
                <select
                  value={form.defaultStimmrechtsart}
                  onChange={(e) => setForm({ ...form, defaultStimmrechtsart: e.target.value as "kopf" | "stimmrecht" | "mea" })}
                >
                  <option value="kopf">Köpfe</option>
                  <option value="stimmrecht">Stimmrecht</option>
                  <option value="mea">MEA</option>
                </select>
              </div>
              <div className="form-field">
                <label>Standard Quorum</label>
                <select
                  value={form.defaultQuorum}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      defaultQuorum: e.target.value as "allstimmig" | "einfache_mehrheit" | "qualifizierte_mehrheit"
                    })
                  }
                >
                  <option value="allstimmig">Allstimmig</option>
                  <option value="einfache_mehrheit">Einfache Mehrheit</option>
                  <option value="qualifizierte_mehrheit">Qualifizierte Mehrheit</option>
                </select>
              </div>
              <div className="form-field">
                <label>Facilioo-Nutzung</label>
                <div className="settings-switch-row tenant-onboarding-switch-row">
                  <label className="settings-toggle" htmlFor="tenant-facilioo-default">
                    <input
                      id="tenant-facilioo-default"
                      type="checkbox"
                      checked={form.defaultFaciliooEnabled}
                      onChange={(e) => setForm({ ...form, defaultFaciliooEnabled: e.target.checked })}
                    />
                    <span className="settings-toggle-slider" />
                  </label>
                  <span>{form.defaultFaciliooEnabled ? "Facilioo standardmäßig aktiv" : "Facilioo standardmäßig inaktiv"}</span>
                </div>
              </div>
              <div className="form-field">
                <label>Demozeitraum</label>
                <div className="settings-switch-row tenant-onboarding-switch-row">
                  <label className="settings-toggle" htmlFor="tenant-trial-enabled">
                    <input
                      id="tenant-trial-enabled"
                      type="checkbox"
                      checked={form.trialEnabled}
                      onChange={(e) => setForm({ ...form, trialEnabled: e.target.checked })}
                    />
                    <span className="settings-toggle-slider" />
                  </label>
                  <span>{form.trialEnabled ? "Kostenlosen Demozeitraum aktivieren" : "Kein Demozeitraum"}</span>
                </div>
              </div>
              {form.trialEnabled ? (
                <div className="form-field">
                  <label>Dauer (Tage)</label>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={form.trialDays}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        trialDays: Math.max(1, Math.min(365, Number(e.target.value || 1)))
                      })
                    }
                  />
                </div>
              ) : null}
            </div>
          </div>

          <div className="tenant-onboarding-section">
            <h4>Erster Admin</h4>
            <div className="tenant-onboarding-grid">
              <div className="form-field">
                <label>Benutzername*</label>
                <input value={form.adminUsername} onChange={(e) => setForm({ ...form, adminUsername: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Anzeigename*</label>
                <input value={form.adminDisplayName} onChange={(e) => setForm({ ...form, adminDisplayName: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Passwort* (min. 8 Zeichen)</label>
                <div className="owner-auth-password-row">
                  <input
                    type={showAdminPassword ? "text" : "password"}
                    value={form.adminPassword}
                    onChange={(e) => setForm({ ...form, adminPassword: e.target.value })}
                  />
                  <button
                    type="button"
                    className="owner-auth-eye-btn"
                    onClick={() => setShowAdminPassword((value) => !value)}
                    aria-label={showAdminPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                    title={showAdminPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                  >
                    {showAdminPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                          d="M2 12C3.8 7.8 7.5 5 12 5c4.5 0 8.2 2.8 10 7-1.8 4.2-5.5 7-10 7-4.5 0-8.2-2.8-10-7Z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                          d="M2 12C3.8 7.8 7.5 5 12 5c4.5 0 8.2 2.8 10 7-1.8 4.2-5.5 7-10 7-4.5 0-8.2-2.8-10-7Z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          </div>
          <div className="form-actions">
            <button disabled={!canCreate || createMutation.isPending} onClick={() => createMutation.mutate()}>
              {createMutation.isPending ? "Onboarding läuft..." : "Mandant anlegen"}
            </button>
            {createError ? <span className="error-text">{createError}</span> : null}
          </div>
          {createInfo ? <div style={{ marginTop: 8, color: "var(--text-soft)" }}>{createInfo}</div> : null}
          {lastAccessUrl ? <div style={{ marginTop: 8, color: "var(--text-soft)" }}>{`Mandant erreichbar unter: ${lastAccessUrl}`}</div> : null}
        </section>
      ) : null}

      {editingSlug ? (
        <section className={`panel create-form ${isFrameworkV2 ? "platform-v2-form-panel" : ""}`}>
          <h3 style={{ marginTop: 0 }}>{`Mandant bearbeiten: ${editingSlug}`}</h3>
          <div className="tenant-onboarding-section">
            <h4>Stripe-Abrechnung</h4>
            {billingLoading ? (
              <div style={{ color: "var(--text-soft)" }}>Lade Billing-Status...</div>
            ) : billingIsError ? (
              <div className="error-text">{billingError instanceof Error ? billingError.message : "Billing-Status konnte nicht geladen werden."}</div>
            ) : billingStatus ? (
              <>
                <div className="tenant-onboarding-grid">
                  <div className="form-field">
                    <label>Status</label>
                    <div>{billingStatusBadge(billingStatus.status)}</div>
                  </div>
                  <div className="form-field">
                    <label>Aktive Einheiten</label>
                    <div>{billingStatus.activeUnitCount.toLocaleString("de-DE")}</div>
                  </div>
                  <div className="form-field">
                    <label>Aktueller Tarif</label>
                    <div>{billingTierLabel(billingStatus.resolvedTier?.tier ?? billingStatus.currentTier)}</div>
                  </div>
                  <div className="form-field">
                    <label>Letzter Snapshot</label>
                    <div>{billingStatus.lastSnapshot ? formatDateTime(billingStatus.lastSnapshot.snapshotDate) : "-"}</div>
                  </div>
                  <div className="form-field">
                    <label>Stripe Customer</label>
                    <div style={{ wordBreak: "break-word" }}>{billingStatus.stripeCustomerId ?? "-"}</div>
                  </div>
                  <div className="form-field">
                    <label>Stripe Subscription</label>
                    <div style={{ wordBreak: "break-word" }}>{billingStatus.stripeSubscriptionId ?? "-"}</div>
                  </div>
                </div>
                {!billingStatus.configured ? (
                  <div className="error-text" style={{ marginTop: 8 }}>
                    Stripe Billing ist noch nicht vollständig konfiguriert. Bitte Secret Key und Price-IDs in der Backend-Umgebung setzen.
                  </div>
                ) : null}
                <div className="form-actions" style={{ marginTop: 10 }}>
                  <button
                    type="button"
                    disabled={!billingStatus.configured || checkoutBillingMutation.isPending || portalBillingMutation.isPending || syncBillingMutation.isPending}
                    onClick={() => {
                      if (!editingSlug) return;
                      checkoutBillingMutation.mutate(editingSlug);
                    }}
                  >
                    {checkoutBillingMutation.isPending ? "Erstellt..." : "Checkout-Link erstellen"}
                  </button>
                  <button
                    type="button"
                    disabled={!billingStatus.configured || portalBillingMutation.isPending || checkoutBillingMutation.isPending || syncBillingMutation.isPending}
                    onClick={() => {
                      if (!editingSlug) return;
                      portalBillingMutation.mutate(editingSlug);
                    }}
                  >
                    {portalBillingMutation.isPending ? "Öffnet..." : "Stripe Portal öffnen"}
                  </button>
                  <button
                    type="button"
                    disabled={!billingStatus.configured || syncBillingMutation.isPending || checkoutBillingMutation.isPending || portalBillingMutation.isPending}
                    onClick={() => {
                      if (!editingSlug) return;
                      syncBillingMutation.mutate(editingSlug);
                    }}
                  >
                    {syncBillingMutation.isPending ? "Synchronisiert..." : "Billing neu synchronisieren"}
                  </button>
                </div>
              </>
            ) : null}
          </div>
          <div className="tenant-onboarding-grid">
            <div className="form-field">
              <label>Mandantenname*</label>
              <input value={editForm.name} onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Status</label>
              <select
                value={editForm.status}
                onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value as "aktiv" | "inaktiv" }))}
              >
                <option value="aktiv">aktiv</option>
                <option value="inaktiv">inaktiv</option>
              </select>
            </div>
            <div className="form-field">
              <label>Rechtsform</label>
              <input value={editForm.legalForm} onChange={(e) => setEditForm((prev) => ({ ...prev, legalForm: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Website</label>
              <input value={editForm.website} onChange={(e) => setEditForm((prev) => ({ ...prev, website: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Kontakt-E-Mail*</label>
              <input value={editForm.contactEmail} onChange={(e) => setEditForm((prev) => ({ ...prev, contactEmail: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Kontakt-Telefon</label>
              <input value={editForm.contactPhone} onChange={(e) => setEditForm((prev) => ({ ...prev, contactPhone: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Straße</label>
              <input value={editForm.addressStreet} onChange={(e) => setEditForm((prev) => ({ ...prev, addressStreet: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>PLZ</label>
              <input value={editForm.addressPostalCode} onChange={(e) => setEditForm((prev) => ({ ...prev, addressPostalCode: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Ort</label>
              <input value={editForm.addressCity} onChange={(e) => setEditForm((prev) => ({ ...prev, addressCity: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Land</label>
              <input value={editForm.addressCountry} onChange={(e) => setEditForm((prev) => ({ ...prev, addressCountry: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Anrede Ansprechpartner</label>
              <input value={editForm.contactSalutation} onChange={(e) => setEditForm((prev) => ({ ...prev, contactSalutation: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Titel Ansprechpartner</label>
              <input value={editForm.contactTitle} onChange={(e) => setEditForm((prev) => ({ ...prev, contactTitle: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Vorname Ansprechpartner</label>
              <input value={editForm.contactFirstName} onChange={(e) => setEditForm((prev) => ({ ...prev, contactFirstName: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Nachname Ansprechpartner</label>
              <input value={editForm.contactLastName} onChange={(e) => setEditForm((prev) => ({ ...prev, contactLastName: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>E-Mail Ansprechpartner</label>
              <input value={editForm.contactPersonEmail} onChange={(e) => setEditForm((prev) => ({ ...prev, contactPersonEmail: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Telefon Ansprechpartner</label>
              <input value={editForm.contactPersonPhone} onChange={(e) => setEditForm((prev) => ({ ...prev, contactPersonPhone: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Zeitzone</label>
              <input value={editForm.technicalTimezone} onChange={(e) => setEditForm((prev) => ({ ...prev, technicalTimezone: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Locale</label>
              <input value={editForm.technicalLocale} onChange={(e) => setEditForm((prev) => ({ ...prev, technicalLocale: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Währung</label>
              <input value={editForm.technicalCurrency} onChange={(e) => setEditForm((prev) => ({ ...prev, technicalCurrency: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>UI-Framework</label>
              <select
                value={editForm.technicalUiFramework}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    technicalUiFramework: e.target.value === "mockup_v2" ? "mockup_v2" : "classic"
                  }))
                }
              >
                <option value="classic">Classic (bisherig)</option>
                <option value="mockup_v2">Mockup V2 (neu)</option>
              </select>
            </div>
            <div className="form-field">
              <label>Standard Stimmrechtsart</label>
              <select
                value={editForm.defaultStimmrechtsart}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    defaultStimmrechtsart: e.target.value as "kopf" | "stimmrecht" | "mea"
                  }))
                }
              >
                <option value="kopf">Köpfe</option>
                <option value="stimmrecht">Stimmrecht</option>
                <option value="mea">MEA</option>
              </select>
            </div>
            <div className="form-field">
              <label>Standard Quorum</label>
              <select
                value={editForm.defaultQuorum}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    defaultQuorum: e.target.value as "allstimmig" | "einfache_mehrheit" | "qualifizierte_mehrheit"
                  }))
                }
              >
                <option value="allstimmig">Allstimmig</option>
                <option value="einfache_mehrheit">Einfache Mehrheit</option>
                <option value="qualifizierte_mehrheit">Qualifizierte Mehrheit</option>
              </select>
            </div>
            <div className="form-field">
              <label>Facilioo-Nutzung</label>
              <div className="settings-switch-row tenant-onboarding-switch-row">
                <label className="settings-toggle" htmlFor="tenant-edit-facilioo-default">
                  <input
                    id="tenant-edit-facilioo-default"
                    type="checkbox"
                    checked={editForm.defaultFaciliooEnabled}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, defaultFaciliooEnabled: e.target.checked }))}
                  />
                  <span className="settings-toggle-slider" />
                </label>
                <span>{editForm.defaultFaciliooEnabled ? "Facilioo standardmäßig aktiv" : "Facilioo standardmäßig inaktiv"}</span>
              </div>
            </div>
            <div className="form-field">
              <label>Demozeitraum</label>
              <div className="settings-switch-row tenant-onboarding-switch-row">
                <label className="settings-toggle" htmlFor="tenant-edit-trial-enabled">
                  <input
                    id="tenant-edit-trial-enabled"
                    type="checkbox"
                    checked={editForm.trialEnabled}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, trialEnabled: e.target.checked }))}
                  />
                  <span className="settings-toggle-slider" />
                </label>
                <span>{editForm.trialEnabled ? "Kostenlosen Demozeitraum aktivieren" : "Kein Demozeitraum"}</span>
              </div>
            </div>
            {editForm.trialEnabled ? (
              <div className="form-field">
                <label>Dauer (Tage)</label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={editForm.trialDays}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      trialDays: Math.max(1, Math.min(365, Number(e.target.value || 1)))
                    }))
                  }
                />
              </div>
            ) : null}
            <div className="tenant-onboarding-section" style={{ gridColumn: "1 / -1" }}>
              <h4>Erster Admin</h4>
              <div className="tenant-onboarding-grid">
                <div className="form-field">
                  <label>Benutzername*</label>
                  <input
                    value={editForm.adminUsername}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, adminUsername: e.target.value }))}
                  />
                </div>
                <div className="form-field">
                  <label>Anzeigename*</label>
                  <input
                    value={editForm.adminDisplayName}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, adminDisplayName: e.target.value }))}
                  />
                </div>
                <div className="form-field">
                  <label>Neues Passwort (optional, min. 8 Zeichen)</label>
                  <div className="owner-auth-password-row">
                    <input
                      type={showEditAdminPassword ? "text" : "password"}
                      value={editForm.adminPassword}
                      autoComplete="new-password"
                      placeholder="Leer lassen, um es nicht zu ändern"
                      onChange={(e) => setEditForm((prev) => ({ ...prev, adminPassword: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="owner-auth-eye-btn"
                      onClick={() => setShowEditAdminPassword((value) => !value)}
                      aria-label={showEditAdminPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                      title={showEditAdminPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                    >
                      <PasswordVisibilityIcon hidden={showEditAdminPassword} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="form-field" style={{ gridColumn: "1 / -1" }}>
              <label>Facilioo Debugsteuerung (nur Plattform)</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div className="settings-switch-row tenant-onboarding-switch-row">
                  <label className="settings-toggle" htmlFor="tenant-edit-facilioo-debug-mode">
                    <input
                      id="tenant-edit-facilioo-debug-mode"
                      type="checkbox"
                      checked={editFaciliooDebug.debugModeEnabled === true}
                      onChange={(e) =>
                        setEditFaciliooDebug((prev) => ({ ...prev, debugModeEnabled: e.target.checked }))
                      }
                      disabled={editFaciliooDebugLoading || updateFaciliooDebugMutation.isPending}
                    />
                    <span className="settings-toggle-slider" />
                  </label>
                  <span>{editFaciliooDebug.debugModeEnabled ? "Debug-Modus aktiv (24h)" : "Debug-Modus inaktiv"}</span>
                </div>
                <small style={{ color: "var(--text-soft)" }}>
                  Ablauf Debug-Modus: {editFaciliooDebug.debugModeEnabled ? formatDateTime(editFaciliooDebug.debugModeExpiresAt) : "-"}
                </small>
                <div className="settings-switch-row tenant-onboarding-switch-row">
                  <label className="settings-toggle" htmlFor="tenant-edit-facilioo-deep-probe">
                    <input
                      id="tenant-edit-facilioo-deep-probe"
                      type="checkbox"
                      checked={editFaciliooDebug.deepProbeEnabled === true}
                      onChange={(e) =>
                        setEditFaciliooDebug((prev) => ({ ...prev, deepProbeEnabled: e.target.checked }))
                      }
                      disabled={editFaciliooDebugLoading || updateFaciliooDebugMutation.isPending}
                    />
                    <span className="settings-toggle-slider" />
                  </label>
                  <span>{editFaciliooDebug.deepProbeEnabled ? "Deep-Probe aktiv (24h)" : "Deep-Probe inaktiv"}</span>
                </div>
                <small style={{ color: "var(--text-soft)" }}>
                  Ablauf Deep-Probe: {editFaciliooDebug.deepProbeEnabled ? formatDateTime(editFaciliooDebug.deepProbeExpiresAt) : "-"}
                </small>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    disabled={Boolean(!editingSlug) || editFaciliooDebugLoading || updateFaciliooDebugMutation.isPending}
                    onClick={() => {
                      if (!editingSlug) return;
                      updateFaciliooDebugMutation.mutate({
                        slug: editingSlug,
                        debugModeEnabled: editFaciliooDebug.debugModeEnabled,
                        deepProbeEnabled: editFaciliooDebug.deepProbeEnabled
                      });
                    }}
                  >
                    {editFaciliooDebugLoading
                      ? "Lade..."
                      : updateFaciliooDebugMutation.isPending
                        ? "Speichert..."
                        : "Debugsteuerung 24h anwenden"}
                  </button>
                  {editFaciliooDebugError ? <span className="error-text">{editFaciliooDebugError}</span> : null}
                  {editFaciliooDebugInfo ? <span style={{ color: "var(--text-soft)" }}>{editFaciliooDebugInfo}</span> : null}
                </div>
              </div>
            </div>
          </div>
          <div className="form-actions">
            <button
              type="button"
              disabled={
                !editForm.name.trim() ||
                editForm.adminUsername.trim().length < 3 ||
                editForm.adminDisplayName.trim().length === 0 ||
                (editForm.adminPassword.trim().length > 0 && editForm.adminPassword.trim().length < 8) ||
                (editForm.contactEmail.trim().length > 0 && !/\S+@\S+\.\S+/.test(editForm.contactEmail.trim())) ||
                updateMutation.isPending
              }
              onClick={() => {
                if (!editingSlug) return;
                updateMutation.mutate({
                  slug: editingSlug,
                  form: editForm
                });
              }}
            >
              {updateMutation.isPending ? "Speichert..." : "Änderungen speichern"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingSlug(null);
                setEditError("");
                setEditFaciliooDebugError("");
                setEditFaciliooDebugInfo("");
                setShowEditAdminPassword(false);
              }}
            >
              Abbrechen
            </button>
            {editError ? <span className="error-text">{editError}</span> : null}
            {editInfo ? <span style={{ color: "var(--text-soft)" }}>{editInfo}</span> : null}
          </div>
        </section>
      ) : null}

      {isError ? <section className="panel error-text">{error instanceof Error ? error.message : "Fehler beim Laden der Mandanten"}</section> : null}
      {tenantProvisioningInfo || tenantProvisioningError ? (
        <section className={`panel ${tenantProvisioningError ? "error-text" : ""}`}>
          {tenantProvisioningError || tenantProvisioningInfo}
        </section>
      ) : null}

      <section className={isFrameworkV2 ? "platform-v2-table-section" : undefined}>
        <ServerDataTable columns={columns} rows={tenantRows} total={tenantRows.length} page={1} pageSize={50} onPageChange={() => undefined} />
      </section>

      {systemRows.length > 0 ? (
        <section className={`panel ${isFrameworkV2 ? "platform-v2-system-panel" : ""}`}>
          <h3 style={{ marginTop: 0 }}>System</h3>
          <section className={isFrameworkV2 ? "platform-v2-table-section" : undefined}>
            <ServerDataTable columns={systemColumns} rows={systemRows} total={systemRows.length} page={1} pageSize={50} onPageChange={() => undefined} />
          </section>
        </section>
      ) : null}

      {isLoading ? <section className="panel">Lade Mandanten...</section> : null}
    </div>
  );
};
